import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Radio, MessageSquare, ChevronLeft, Loader2, Eye, Megaphone,
  StopCircle, Send, UserPlus, Copy, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StreamViewer from "../components/StreamViewer";
import QrOverlayPanel from "../components/QrOverlayPanel";
import GuestManager from "../components/GuestManager";
import CrewBoostButton from "../components/CrewBoostButton";
import { MobileTabBar, MobileTabSection, MobileBottomNav } from "../components/MobileTabBar";
import { Image as ImageIcon, Users as UsersIcon, MessageSquare as ChatIcon, Video as VideoIcon } from "lucide-react";

/**
 * AssistantDirector — second-screen "cockpit" that drives a live stream
 * WITHOUT taking over the broadcaster's camera.
 *
 *   /studio/assist             → list of streams I can pilot (creator + delegated)
 *   /studio/assist/:streamId   → cockpit for a specific stream
 *
 * The original device keeps streaming its camera through LiveBroadcaster on
 * /studio/live. From this page we only read viewer-side video and write
 * director commands (overlays, chat, guests, scenes, ads, crew boost,
 * end-stream). No getUserMedia call ever fires here.
 */
export default function AssistantDirector() {
  const { streamId } = useParams();
  if (streamId) return <Cockpit streamId={streamId} />;
  return <CockpitLanding />;
}

// ---------- Landing — pick a stream to pilot ----------
function CockpitLanding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/studio/assist")}`, { replace: true });
      return;
    }
    api.get("/live/me/directable")
      .then((r) => setList(r.data || []))
      .catch((e) => setErr(formatApiError(e)));
  }, [user, nav]);

  return (
    <div data-testid="assistant-cockpit-landing" className="pt-32 pb-16 px-4 sm:px-8 md:px-12 max-w-3xl mx-auto">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Assistant Director · Cockpit</div>
      <h1 className="font-display font-black text-5xl text-white tracking-tighter mt-2">
        Run the show from this screen
      </h1>
      <p className="text-white/60 mt-3">
        Pick a stream below to take over overlays, chat, guests, scenes and ads. The streaming camera stays on the original device — this is a non-broadcasting control room.
      </p>

      {err && (
        <div className="mt-6 bg-red-500/15 border border-red-500/30 rounded-xl p-4 text-red-200 text-sm" data-testid="cockpit-err">
          {err}
        </div>
      )}

      <div className="mt-8 space-y-2">
        {list === null && (
          <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {Array.isArray(list) && list.length === 0 && (
          <div data-testid="cockpit-empty" className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/65 text-sm leading-relaxed">
            No live streams to pilot right now. Either go live yourself from <Link to="/studio/live" className="text-tv-accent underline">Director's Mode</Link>, or wait for a creator to invite you as their Assistant Director — you'll get an invite link in your inbox.
          </div>
        )}
        {Array.isArray(list) && list.map((s) => (
          <Link
            key={s.id}
            to={`/studio/assist/${s.id}`}
            data-testid={`cockpit-pilot-${s.id}`}
            className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/[0.08] p-4 transition group"
          >
            <div className="w-2.5 h-2.5 rounded-full bg-tv-live animate-pulse shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-display font-bold text-lg leading-tight truncate">
                {s.title || "Untitled live"}
              </div>
              <div className="text-white/55 text-xs mt-0.5">
                Hosted by {s.creator_name || (s.my_role === "creator" ? "you" : "—")} · {(s.viewer_count || 0).toLocaleString()} watching
                {s.my_role === "assistant" && <> · <span className="text-tv-accent">Assistant access</span></>}
                {s.my_role === "creator" && <> · <span className="text-emerald-300">You own this stream</span></>}
              </div>
            </div>
            <div className="text-tv-accent text-xs uppercase tracking-widest font-black opacity-70 group-hover:opacity-100">
              Pilot →
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------- Cockpit — full controls for one stream ----------
function Cockpit({ streamId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stream, setStream] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [guests, setGuests] = useState([]);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState("preview");
  const wsRef = useRef(null);
  const chatRef = useRef(null);

  // Keep stream + guests fresh — poll lightly so the cockpit stays in sync
  // even if the broadcaster device tweaks something locally.
  useEffect(() => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/studio/assist/${streamId}`)}`, { replace: true });
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [s, g] = await Promise.all([
          api.get(`/live/${streamId}`),
          api.get(`/live/${streamId}/guests`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setStream(s.data);
        setGuests(g.data || []);
      } catch (e) {
        if (cancelled) return;
        setLoadErr(formatApiError(e));
      }
    };
    load();
    const id = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [streamId, user, nav]);

  // Hook into the live chat WS so cockpit chat is real-time.
  useEffect(() => {
    if (!stream?.id) return undefined;
    api.get(`/live/${stream.id}/chat`).then((r) => setChat(r.data || [])).catch(() => {});
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const url = `${wsBase}/api/ws/live/${stream.id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    let timer = null;
    let closed = false;
    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === "chat") setChat((p) => [...p.slice(-99), data]);
            if (data.type === "viewer_count") setStream((s) => s ? { ...s, viewer_count: data.viewer_count } : s);
          } catch { /* ignore malformed frames */ }
        };
        ws.onclose = () => { if (!closed) timer = setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      } catch { timer = setTimeout(connect, 3000); }
    };
    connect();
    return () => { closed = true; if (timer) clearTimeout(timer); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, [stream?.id]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat]);

  if (loadErr) {
    return (
      <div className="pt-32 pb-16 px-4 max-w-2xl mx-auto text-center">
        <div className="text-white/70">{loadErr}</div>
        <Link to="/studio/assist" className="inline-block mt-4 text-tv-accent underline text-sm" data-testid="cockpit-back-to-list">← Back to cockpit list</Link>
      </div>
    );
  }
  if (!stream) {
    return (
      <div className="pt-32 pb-16 px-4 text-center text-white/60 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading cockpit…
      </div>
    );
  }

  const sendChat = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setChatBusy(true);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "chat", message: msg }));
      setMsg("");
    } else {
      try { await api.post(`/live/${stream.id}/chat`, { message: msg }); setMsg(""); }
      catch (e2) { toast.error(formatApiError(e2)); }
    }
    setChatBusy(false);
  };

  const changeScene = async (scene) => {
    try { await api.post(`/live/${stream.id}/scene`, { scene }); toast.success(`Scene → ${scene}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const triggerAd = async () => {
    try { await api.post(`/live/${stream.id}/ad`, { duration_sec: 15 }); toast.success("Ad break triggered"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const endStream = async () => {
    setEndBusy(true);
    try {
      await api.post(`/live/${stream.id}/stop`);
      toast.success("Stream ended");
      nav("/studio/assist", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); setEndBusy(false); }
  };

  const refreshGuests = () =>
    api.get(`/live/${stream.id}/guests`).then((r) => setGuests(r.data || [])).catch(() => {});

  return (
    <div data-testid="assistant-cockpit" className="pt-24 sm:pt-28 pb-24 lg:pb-12 px-3 sm:px-6 md:px-8 max-w-[1400px] mx-auto">
      {/* Header bar — flex-wraps on phones so we never overflow horizontally.
          Primary action (End Stream) stays right-aligned; secondaries fold
          into a "More" overflow on phones. */}
      <header className="sticky top-16 z-30 -mx-3 sm:-mx-6 md:-mx-8 px-3 sm:px-6 md:px-8 py-2 bg-black/80 backdrop-blur-md border-b border-white/10 flex items-center gap-2 flex-wrap mb-4">
        <Link to="/studio/assist" className="text-white/55 hover:text-white text-[11px] inline-flex items-center gap-1 shrink-0" data-testid="cockpit-back">
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Cockpit list</span>
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-tv-accent font-black hidden md:inline">Assistant Director</span>
        <span className="inline-flex items-center gap-1.5 bg-tv-live/20 border border-tv-live/40 rounded-full px-2 py-0.5 text-tv-live text-[10px] font-black uppercase tracking-wider shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-tv-live animate-pulse" /> Live
        </span>
        <span className="bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-white/85 text-[10px] inline-flex items-center gap-1 shrink-0" data-testid="cockpit-viewer-count">
          <Eye className="w-3 h-3" /> {(stream.viewer_count || 0).toLocaleString()}
        </span>
        <div className="flex-1" />
        <button
          data-testid="cockpit-invite-assistant"
          onClick={() => setInviteOpen(true)}
          className="hidden sm:inline-flex text-white/65 hover:text-white text-[10px] uppercase tracking-wider font-black items-center gap-1 px-2 py-1"
        ><UserPlus className="w-3.5 h-3.5" /> Add Assistant</button>
        <button
          data-testid="cockpit-end-stream"
          onClick={() => setConfirmEnd(true)}
          className="bg-tv-live hover:bg-red-600 text-white font-black text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full inline-flex items-center gap-1 shrink-0"
        ><StopCircle className="w-3 h-3" /> End</button>
      </header>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4 text-yellow-100/90 text-[11px] leading-snug" data-testid="cockpit-mode-banner">
        Co-piloting <b className="break-words">{stream.title}</b>. The broadcasting camera stays on the original device — this screen only changes overlays, chat, guests, scenes, ads.
      </div>

      <MobileTabBar
        tab={mobileTab}
        setTab={setMobileTab}
        testid="cockpit-mobile-tabs"
        tabs={[
          { id: "preview",  label: "Preview",  icon: VideoIcon },
          { id: "overlays", label: "Overlays", icon: ImageIcon },
          { id: "guests",   label: "Guests",   icon: UsersIcon, badge: guests?.length || 0 },
          { id: "chat",     label: "Chat",     icon: ChatIcon, badge: chat?.length || 0 },
        ]}
      />

      {/* Floating bottom nav — same 4 tabs reachable with one thumb-tap. */}
      <MobileBottomNav
        tab={mobileTab}
        setTab={setMobileTab}
        testid="cockpit-bottom-nav"
        tabs={[
          { id: "preview",  label: "Live",     icon: VideoIcon },
          { id: "overlays", label: "Overlays", icon: ImageIcon },
          { id: "guests",   label: "Guests",   icon: UsersIcon, badge: guests?.length || 0 },
          { id: "chat",     label: "Chat",     icon: ChatIcon, badge: chat?.length || 0 },
        ]}
      />

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-4 sm:gap-5">
        {/* LEFT — preview + scenes + ad + QR + guests */}
        <div className="min-w-0 space-y-3 sm:space-y-4">
          {/* Hoisted out of MobileTabSection so the host's broadcast
              preview ALWAYS stays visible on mobile, regardless of which
              tab is active (matches user's "stream preview should stay
              locked in place" requirement). On lg+ desktop nothing
              changes — preview still renders at the top of the left
              column. */}
          <div data-testid="cockpit-preview" className="lg:static sticky top-16 z-20 bg-black -mx-3 sm:mx-0 lg:mx-0 px-0 lg:px-0">
            <div className="relative aspect-video lg:rounded-xl overflow-hidden bg-black border-y lg:border lg:border-white/10 border-white/10">
              <StreamViewer streamId={stream.id} kidsBrand={stream.category === "kids"} />
            </div>
          </div>

          <MobileTabSection tab={mobileTab} match="preview">
          {/* Scene picker — wraps nicely on phones; ad + crew boost slip to a
              second row so nothing overflows. */}
          <div className="rounded-xl border border-white/10 bg-black/40 p-2.5 sm:p-3" data-testid="cockpit-scene-picker">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-black mb-2">Scene</div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { id: "camera",    label: "Camera" },
                { id: "chat",      label: "Chat" },
                { id: "poll",      label: "Poll" },
                { id: "meme",      label: "Meme" },
                { id: "brb",       label: "BRB" },
              ].map((sc) => (
                <button
                  key={sc.id}
                  data-testid={`cockpit-scene-${sc.id}`}
                  onClick={() => changeScene(sc.id)}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-black uppercase tracking-wider border transition ${
                    stream.scene === sc.id
                      ? "bg-tv-accent text-black border-tv-accent"
                      : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                  }`}
                >{sc.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button
                data-testid="cockpit-trigger-ad"
                onClick={triggerAd}
                className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 font-black text-[10px] sm:text-[11px] uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border border-yellow-500/40"
              ><Megaphone className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Trigger ad</button>
              {user?.role === "admin" && (
                <CrewBoostButton streamId={stream.id} compact />
              )}
            </div>
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="overlays">
          <QrOverlayPanel streamId={stream.id} currentOverlays={stream.overlays} />
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="guests">
          <GuestManager
            streamId={stream.id}
            inviteCode={stream.invite_code}
            guests={guests}
            onRefresh={refreshGuests}
          />
          </MobileTabSection>
        </div>

        {/* RIGHT — chat + assistants list */}
        <aside className="space-y-3 sm:space-y-4 min-w-0">
          <MobileTabSection tab={mobileTab} match="chat">
          <div className="rounded-xl border border-white/10 bg-black/40 p-2.5 sm:p-3 flex flex-col h-[460px] lg:h-[440px]">
            <div className="flex items-center gap-2 text-white/85 text-[11px] uppercase tracking-[0.25em] font-black mb-2">
              <MessageSquare className="w-3.5 h-3.5" /> Live Chat
            </div>
            <div ref={chatRef} data-testid="cockpit-chat-list" className="flex-1 overflow-y-auto space-y-2 pr-1">
              {chat.length === 0 && <div className="text-white/40 text-xs">Quiet so far…</div>}
              {chat.map((m) => (
                <div key={m.id} className="flex gap-2 items-start text-sm" data-testid={`cockpit-chat-msg-${m.id}`}>
                  <div className="w-6 h-6 rounded-full bg-tv-brand text-black font-bold flex items-center justify-center text-xs shrink-0">
                    {(m.user_name || "?")[0]}
                  </div>
                  <div className="min-w-0">
                    <span className="text-tv-brand font-semibold text-xs">{m.user_name}</span>
                    <span className="text-white/85 ml-1.5 break-words">{m.message}</span>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendChat} className="mt-2 flex gap-1">
              <input
                data-testid="cockpit-chat-input"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Message as the host"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm text-white outline-none"
              />
              <button
                data-testid="cockpit-chat-send"
                disabled={chatBusy}
                className="bg-tv-accent disabled:opacity-50 text-black font-bold px-3 rounded-full text-xs inline-flex items-center gap-1"
              ><Send className="w-3 h-3" /> Send</button>
            </form>
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="chat">
          <CockpitAssistantList streamId={stream.id} ownerOrAdmin={user?.user_id === stream.creator_id || user?.role === "admin"} />

          <div className="rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-white/55 flex items-start gap-2 mt-3 lg:mt-0">
            <Clock className="w-3.5 h-3.5 mt-0.5 text-white/35" />
            <div>
              Tip: keep this tab visible — actions take effect immediately. To hand control back, just close this tab; the broadcaster device keeps streaming.
            </div>
          </div>
          </MobileTabSection>
        </aside>
      </div>

      {confirmEnd && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cockpit-end-confirm">
          <div className="bg-[#0B0F16] border border-white/15 rounded-2xl p-6 max-w-md w-full">
            <div className="text-tv-live text-[10px] uppercase tracking-widest font-black mb-1">End the stream?</div>
            <h3 className="font-display font-black text-2xl text-white">Cut the broadcast for everyone?</h3>
            <p className="text-white/55 text-sm mt-2">
              This stops the stream on every device — the creator's broadcasting tab will see "Stream ended". This action can't be undone.
            </p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setConfirmEnd(false)} className="flex-1 bg-white/5 hover:bg-white/10 text-white font-bold py-2.5 rounded-full text-sm" data-testid="cockpit-end-cancel">Cancel</button>
              <button onClick={endStream} disabled={endBusy} className="flex-1 bg-tv-live hover:bg-red-600 text-white font-bold py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="cockpit-end-confirm-btn">
                {endBusy ? "Ending…" : "End now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <InviteAssistantModal
          streamId={stream.id}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

// ---------- Existing assistants list (revoke) ----------
function CockpitAssistantList({ streamId, ownerOrAdmin }) {
  const [list, setList] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get(`/live/${streamId}/assistants`)
      .then((r) => setList(r.data?.assistants || []))
      .catch(() => setList([]));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [streamId]);

  const revoke = async (a) => {
    if (!ownerOrAdmin) return;
    setBusyId(a.user_id);
    try {
      await api.delete(`/live/${streamId}/assistant/${a.user_id}`);
      toast.success(`${a.name || "Assistant"} removed`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid="cockpit-assistants-list">
      <div className="text-white/85 text-[11px] uppercase tracking-[0.25em] font-black mb-2 inline-flex items-center gap-1.5">
        <UserPlus className="w-3.5 h-3.5" /> Assistants on duty
      </div>
      {list.length === 0 && (
        <div className="text-white/40 text-xs">No active assistants.</div>
      )}
      {list.map((a) => (
        <div key={a.user_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-b-0" data-testid={`cockpit-assistant-${a.user_id}`}>
          <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
          <div className="text-sm text-white truncate flex-1">{a.name || a.user_id}</div>
          {ownerOrAdmin && (
            <button
              data-testid={`cockpit-assistant-revoke-${a.user_id}`}
              onClick={() => revoke(a)}
              disabled={busyId === a.user_id}
              className="text-red-300/70 hover:text-red-200 text-[10px] uppercase tracking-wider font-black"
            >Revoke</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Invite modal ----------
function InviteAssistantModal({ streamId, onClose }) {
  const [username, setUsername] = useState("");
  const [hours, setHours] = useState(4);
  const [emailToo, setEmailToo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/live/${streamId}/assistant/invite`, {
        username: username.trim(),
        expires_minutes: Math.max(5, Math.min(60 * 24, Math.round(hours * 60))),
        email_notify: emailToo,
      });
      setIssued(data);
      toast.success(`Assistant invite sent to ${data.assistant?.name || username}`);
    } catch (e2) {
      const code = e2?.response?.status;
      if (code === 404) toast.error(`No Tap In account matches "${username}"`);
      else if (code === 400) toast.error(formatApiError(e2) || "Couldn't send invite — check the username");
      else toast.error(formatApiError(e2));
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cockpit-invite-modal">
      <div className="bg-[#0B0F16] border border-white/15 rounded-2xl p-6 max-w-md w-full">
        <div className="text-tv-accent text-[10px] uppercase tracking-widest font-black mb-1">Add Assistant Director</div>
        <h3 className="font-display font-black text-2xl text-white">Delegate the cockpit</h3>
        <p className="text-white/55 text-sm mt-1">
          Send another Tap In account a time-limited invite. They'll get a notification and a "Cockpit" link — the original streaming device keeps broadcasting.
        </p>

        {!issued ? (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 font-black mb-1">Tap In username or display name</div>
              <input
                data-testid="cockpit-invite-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. zizi"
                autoFocus
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-accent"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 font-black mb-1">Access expires after (hours)</div>
              <input
                data-testid="cockpit-invite-hours"
                type="number" min={0.25} max={24} step={0.25}
                value={hours}
                onChange={(e) => setHours(parseFloat(e.target.value) || 4)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-accent"
              />
            </label>
            <label className="flex items-center gap-2 text-[12px] text-white/65">
              <input
                type="checkbox"
                data-testid="cockpit-invite-email"
                checked={emailToo}
                onChange={(e) => setEmailToo(e.target.checked)}
                className="accent-tv-accent"
              /> Also email them the invite link
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 bg-white/5 hover:bg-white/10 text-white font-bold py-2.5 rounded-full text-sm">Cancel</button>
              <button type="submit" disabled={busy || !username.trim()} data-testid="cockpit-invite-submit" className="flex-1 bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2.5 rounded-full text-sm disabled:opacity-50">
                {busy ? "Sending…" : "Send invite"}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 space-y-3" data-testid="cockpit-invite-issued">
            <div className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-100 rounded-xl p-3 text-sm">
              <b>{issued.assistant?.name || username}</b> was invited. They'll get a notification with a one-tap cockpit link.
            </div>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 font-black mb-1">Direct link (you can DM/text it too)</div>
              <div className="flex gap-1">
                <input readOnly value={issued.redeem_url || ""} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white" />
                <button
                  data-testid="cockpit-invite-copy"
                  type="button"
                  onClick={() => { try { navigator.clipboard.writeText(issued.redeem_url || ""); toast.success("Link copied"); } catch { /* ignore */ } }}
                  className="bg-white/10 hover:bg-white/15 text-white px-3 rounded-lg text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1"
                ><Copy className="w-3.5 h-3.5" /> Copy</button>
              </div>
            </label>
            <button onClick={onClose} className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2.5 rounded-full text-sm">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
